import type { AttackProfile, GearSlot, Item, ItemKind } from '../content/types.ts';
import type { StatId } from '../domain/stats.ts';
import { isStatId } from '../domain/stats.ts';
import type { DamageType } from '../rules/damage.ts';
import { DAMAGE_TYPES, isDamageType } from '../rules/damage.ts';
import type {
  CustomItemDraft,
  CustomItemModifier,
  CustomItemProblem,
} from '../rules/customItems.ts';
import { checkCustomItemDraft, exactDraftFromItem } from '../rules/customItems.ts';

export type ItemWeaponChoice = 'unselected' | 'none' | 'linked';
export type ItemUseMode = 'manual' | 'restore-mana-full';

export type ItemModifierKind = CustomItemModifier['kind'];

export interface ItemModifierEditorState {
  kind: ItemModifierKind;
  amount: string;
  stat: StatId;
  skillId: string;
  damageType: DamageType;
}

export interface CustomItemEditorState {
  goldValue?: string;
  name: string;
  kind: ItemKind;
  slot: GearSlot | '';
  handUse: 'held' | 'worn';
  weaponChoice: ItemWeaponChoice;
  weaponAttackId: string;
  consumable: boolean;
  useMode: ItemUseMode;
  description: string;
  notes: string;
  modifiers: ItemModifierEditorState[];
}

export function emptyItemModifierEditorState(): ItemModifierEditorState {
  return {
    kind: 'damage-resistance',
    amount: '1',
    stat: 'str',
    skillId: '',
    damageType: DAMAGE_TYPES[0],
  };
}

export function emptyCustomItemEditorState(): CustomItemEditorState {
  return {
    name: '',
    kind: 'gear',
    slot: '',
    handUse: 'held',
    weaponChoice: 'unselected',
    weaponAttackId: '',
    consumable: false,
    useMode: 'manual',
    description: '',
    notes: '',
    modifiers: [],
  };
}

function modifierStateFromStored(modifier: CustomItemModifier): ItemModifierEditorState {
  const base = emptyItemModifierEditorState();
  switch (modifier.kind) {
    case 'stat':
      return { ...base, kind: 'stat', stat: modifier.stat, amount: String(modifier.amount) };
    case 'skill-rank':
      return { ...base, kind: 'skill-rank', skillId: modifier.skillId, amount: String(modifier.amount) };
    case 'elemental-resistance':
    case 'vulnerability':
    case 'immunity':
      return { ...base, kind: modifier.kind, damageType: modifier.damageType, amount: '' };
    case 'advantage':
    case 'disadvantage':
      return { ...base, kind: modifier.kind, amount: '' };
    default:
      return { ...base, kind: modifier.kind, amount: String(modifier.amount) };
  }
}

export function editorStateFromItem(item: Item): CustomItemEditorState {
  const converted = exactDraftFromItem(item);
  if (!converted.ok) throw new Error(converted.message);
  const draft = converted.draft;
  const heldHands = draft.slot === 'hands' && draft.handUse === 'held';
  return {
    name: draft.name,
    kind: draft.kind,
    slot: draft.slot ?? '',
    handUse: draft.handUse,
    weaponChoice: heldHands ? (draft.weaponAttackId === null ? 'none' : 'linked') : 'unselected',
    weaponAttackId: draft.weaponAttackId ?? '',
    consumable: draft.consumable,
    useMode: draft.use?.kind === 'restore-mana-full' ? 'restore-mana-full' : 'manual',
    description: draft.description,
    notes: draft.notes ?? '',
    goldValue: draft.goldValue === undefined ? '' : String(draft.goldValue),
    modifiers: draft.modifiers.map(modifierStateFromStored),
  };
}

/**
 * Placement is structural editor state. Moving away from a held Hands item
 * discards the physical-weapon answer so returning to Held requires an explicit
 * answer again rather than resurrecting hidden mechanics.
 */
export function changeItemPlacement(
  state: CustomItemEditorState,
  slot: GearSlot | '',
  handUse: 'held' | 'worn' = state.handUse,
): CustomItemEditorState {
  const heldHands = slot === 'hands' && handUse === 'held';
  return {
    ...state,
    slot,
    handUse,
    ...(heldHands ? {} : { weaponChoice: 'unselected' as const, weaponAttackId: '' }),
  };
}

function parseWholeNonzero(value: string): number | null {
  if (!/^[+-]?\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed !== 0 ? parsed : null;
}

function modifierFromState(
  state: ItemModifierEditorState,
): { ok: true; modifier: CustomItemModifier } | { ok: false; message: string } {
  switch (state.kind) {
    case 'advantage':
    case 'disadvantage':
      return { ok: true, modifier: { kind: state.kind } };
    case 'elemental-resistance':
    case 'vulnerability':
    case 'immunity':
      return isDamageType(state.damageType)
        ? { ok: true, modifier: { kind: state.kind, damageType: state.damageType } }
        : { ok: false, message: 'Choose a real DCC Damage Type.' };
    case 'stat': {
      const amount = parseWholeNonzero(state.amount);
      if (!isStatId(state.stat)) return { ok: false, message: 'Choose a Stat.' };
      return amount === null
        ? { ok: false, message: 'Stat modifiers must be whole nonzero numbers.' }
        : { ok: true, modifier: { kind: 'stat', stat: state.stat, amount } };
    }
    case 'skill-rank': {
      const amount = parseWholeNonzero(state.amount);
      if (state.skillId.trim() === '') return { ok: false, message: 'Choose a Skill.' };
      return amount === null
        ? { ok: false, message: 'Skill Rank modifiers must be whole nonzero numbers.' }
        : {
            ok: true,
            modifier: { kind: 'skill-rank', skillId: state.skillId, amount },
          };
    }
    default: {
      const amount = parseWholeNonzero(state.amount);
      return amount === null
        ? { ok: false, message: 'Additive modifiers must be whole nonzero numbers.' }
        : { ok: true, modifier: { kind: state.kind, amount } };
    }
  }
}

export type PreparedItemDraft =
  | { ok: true; draft: CustomItemDraft }
  | { ok: false; problems: CustomItemProblem[] };

/**
 * Turns form strings into the canonical backend draft. Persisted types never
 * learn about `unselected`: unanswered physical weapon identity is a UI state,
 * and Save is refused before the domain draft exists.
 */
export function prepareCustomItemEditorDraft(
  state: CustomItemEditorState,
  existingItems: readonly Item[],
  editingId: string | null,
  lookupAttack: (id: string) => AttackProfile | undefined,
  lookupSkill: (id: string) => unknown,
): PreparedItemDraft {
  const localProblems: CustomItemProblem[] = [];
  const heldHands = state.slot === 'hands' && state.handUse === 'held';
  if (heldHands && state.weaponChoice === 'unselected') {
    localProblems.push({
      field: 'weaponAttackId',
      message: 'Choose whether this held Hands Item is a physical weapon.',
    });
  }
  if (heldHands && state.weaponChoice === 'linked' && state.weaponAttackId.trim() === '') {
    localProblems.push({ field: 'weaponAttackId', message: 'Choose the Attack this Item is.' });
  }

  const modifiers: CustomItemModifier[] = [];
  for (const row of state.modifiers) {
    const converted = modifierFromState(row);
    if (!converted.ok) {
      localProblems.push({ field: 'modifiers', message: converted.message });
    } else {
      modifiers.push(converted.modifier);
    }
  }
  if (localProblems.length > 0) return { ok: false, problems: localProblems };

  const draft: CustomItemDraft = {
    name: state.name,
    kind: state.kind,
    slot: state.slot === '' ? null : state.slot,
    handUse: state.handUse,
    weaponAttackId:
      heldHands && state.weaponChoice === 'linked' ? state.weaponAttackId : null,
    consumable: state.consumable,
    use: state.useMode === 'restore-mana-full' ? { kind: 'restore-mana-full' } : null,
    description: state.description,
    ...(state.goldValue === undefined || state.goldValue.trim() === '' ? {} : { goldValue: Number(state.goldValue) }),
    notes: state.notes,
    modifiers,
  };
  const problems = checkCustomItemDraft(
    draft,
    existingItems,
    editingId,
    lookupAttack,
    lookupSkill,
  );
  return problems.length === 0 ? { ok: true, draft } : { ok: false, problems };
}
